import React from 'react';
import { Timeline, Card, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { DealActivityDto, DealNoteDto } from 'api/models';
import FeatherIcon from 'feather-icons-react';
import { formatDate, truncate } from 'utility/utility';
import styled from 'styled-components';

const TimelineItem = styled.div`
  .title {
    font-weight: 500;
    margin-bottom: 4px;
  }

  .meta {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 8px;
  }

  .content {
    font-size: 14px;
  }
`;

interface Props {
  activities?: DealActivityDto[];
  notes?: DealNoteDto[];
}

const DealTimeline: React.FC<Props> = ({ activities = [], notes = [] }) => {
  const { t } = useTranslation();

  // Combine and sort activities and notes
  const timelineItems = [
    ...activities.map(activity => ({
      type: 'activity',
      date: activity.createdAt,
      data: activity,
    })),
    ...notes.map(note => ({
      type: 'note',
      date: note.createdAt,
      data: note,
    })),
  ].sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());

  if (timelineItems.length === 0) {
    return (
      <Empty
        image={<FeatherIcon icon="clock" size={40} />}
        description={t('deals:no-timeline', 'No timeline items yet')}
      />
    );
  }

  return (
    <Timeline style={{ paddingTop: '16px' }}>
      {timelineItems.map((item, index) => (
        <Timeline.Item
          key={index}
          dot={
            <FeatherIcon
              icon={item.type === 'activity' ? 'clipboard' : 'message-circle'}
              size={16}
              color={item.type === 'activity' ? '#1890ff' : '#52c41a'}
            />
          }
        >
          <TimelineItem>
            <div className="title">
              {item.type === 'activity' ? (item.data as DealActivityDto).activity : t('deals:note-added', 'Note Added')}
            </div>

            <div className="meta">
              {item.type === 'activity' && <span>{(item.data as DealActivityDto).userName} • </span>}
              {formatDate(item.date)}
            </div>

            {item.type === 'note' && <div className="content">{truncate((item.data as DealNoteDto).content!, 40)}</div>}
          </TimelineItem>
        </Timeline.Item>
      ))}
    </Timeline>
  );
};

export default DealTimeline;
